import { useEffect, useRef, useState } from "react";
import { getDonationStatus } from "@/lib/api";
import { PAYMENT_POLL_MAX_ATTEMPTS, PAYMENT_POLL_INTERVAL_MS } from "@/lib/constants";

function classifyStatus(data) {
  if (data.payment_status === "paid") return { kind: "success", data, error: null };
  if (data.status === "expired") return { kind: "expired", data, error: null };
  return null;
}

async function pollOnce(sessionId) {
  try {
    const data = await getDonationStatus(sessionId);
    return { ok: true, data };
  } catch (e) {
    return {
      ok: false,
      error: e?.response?.data?.detail || e?.message || "Something went wrong",
    };
  }
}

// Custom hook: polls the donation status endpoint until paid, expired, timed out, or errored.
export function usePaymentStatusPoller(sessionId) {
  const [state, setState] = useState({ kind: "checking", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ kind: "error", error: "Missing session id.", data: null });
      return undefined;
    }
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      const result = await pollOnce(sessionId);
      if (cancelled) return;

      if (!result.ok) {
        setState({ kind: "error", data: null, error: result.error });
        return;
      }
      const final = classifyStatus(result.data);
      if (final) {
        setState(final);
        return;
      }
      if (attemptsRef.current >= PAYMENT_POLL_MAX_ATTEMPTS) {
        setState({ kind: "timeout", data: result.data, error: null });
        return;
      }
      setState({ kind: "checking", data: result.data, error: null });
      setTimeout(run, PAYMENT_POLL_INTERVAL_MS);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return state;
}
